
import { Post } from './posts/types';
import { usePostData } from './posts/usePostData';
import { usePostOperations } from './posts/usePostOperations';
import { supabase } from '@/integrations/supabase/client';

interface ZoneFilters {
  activeTab?: 'climate' | 'hardiness';
  climateZone?: string;
  country?: string;
  hardinessZone?: string;
  showMyZoneOnly?: boolean;
}

export const usePosts = (zoneFilters?: ZoneFilters) => {
  const { posts, loading, error, fetchPosts, setPosts } = usePostData(zoneFilters);
  const { deletePost: deletePostOperation, updatePost: updatePostOperation, toggleLike: toggleLikeOperation } = usePostOperations();

  const addPost = async (postData: {
    content: string;
    whats_happening?: string;
    challenges?: string;
    image_url?: string;
    is_video?: boolean;
    crop_type?: string;
    harvest_week_month?: string;
    category?: string;
    location_lat?: number;
    location_lng?: number;
    location_address?: string;
    sentiment?: string;
    crop_plan_info?: any;
    video_metadata?: {
      connection_mode: string;
      is_lite_optimized: boolean;
      max_quality: string;
      bitrate_cap: string;
      preload_setting: string;
    };
  }) => {
    try {
      // Try to get user with retry logic
      let user = null;
      let attempts = 0;
      const maxAttempts = 3;

      while (!user && attempts < maxAttempts) {
        console.log(`Authentication attempt ${attempts + 1}/${maxAttempts}`);
        const { data: { user: currentUser }, error } = await supabase.auth.getUser();
        
        if (error) {
          console.error('Auth error:', error);
          // Try to refresh the session
          await supabase.auth.refreshSession();
        } else {
          user = currentUser;
        }
        
        if (!user) {
          attempts++;
          if (attempts < maxAttempts) {
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      if (!user) {
        console.error('No user found after authentication attempts');
        throw new Error('User authentication failed. Please try logging in again.');
      }

      // Check if user has a profile
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('❌ Error checking user profile:', profileError);
        throw new Error('Failed to verify user profile');
      }

      if (!userProfile) {
        throw new Error('Please complete your profile setup before creating a post');
      }

      console.log('User authenticated successfully:', user.id);

      const cleanPostData: any = {
        user_id: user.id,
        content: postData.content,
        post_type: 'video',
        sentiment: postData.sentiment || 'standard',
        is_video: postData.is_video || false,
        likes_count: 0,
        comments_count: 0
      };

      // Add optional fields only if they have valid values
      if (postData.image_url) cleanPostData.image_url = postData.image_url;
      if (postData.crop_type) cleanPostData.crop_type = postData.crop_type;
      if (postData.harvest_week_month) cleanPostData.harvest_week_month = postData.harvest_week_month;
      if (postData.category) cleanPostData.category = postData.category;
      if (postData.location_address) cleanPostData.location_address = postData.location_address;
      if (postData.location_lat !== undefined) cleanPostData.location_lat = postData.location_lat;
      if (postData.location_lng !== undefined) cleanPostData.location_lng = postData.location_lng;
      if (postData.whats_happening) cleanPostData.whats_happening = postData.whats_happening;
      if (postData.challenges) cleanPostData.challenges = postData.challenges;
      if (postData.crop_plan_info) {
        cleanPostData.crop_plan_info = postData.crop_plan_info;
      }
      // Add video metadata for lite mode optimization tracking
      if (postData.video_metadata) {
        cleanPostData.video_metadata = postData.video_metadata;
      }

      console.log('Inserting post data:', cleanPostData);

      const { data, error } = await supabase
        .from('posts')
        .insert(cleanPostData)
        .select('*')
        .single();

      if (error) {
        console.error('Database insert error:', error);
        throw error;
      }

      console.log('Post created successfully:', data);
      await fetchPosts();
      return data;
    } catch (err) {
      console.error('Error adding post:', err);
      throw err;
    }
  };

  const deletePost = async (postId: string | number) => {
    console.log('=== MAIN DELETE POST DEBUG START ===');
    console.log('Deleting post with ID:', postId);
    
    try {
      console.log('Calling deletePostOperation...');
      await deletePostOperation(postId);
      console.log('deletePostOperation completed successfully');
      
      const postIdString = typeof postId === 'number' ? postId.toString() : postId;
      console.log('Removing post from local state...');
      setPosts(prevPosts => {
        const filteredPosts = prevPosts.filter(post => post.id !== postIdString);
        console.log('Posts before filter:', prevPosts.length);
        console.log('Posts after filter:', filteredPosts.length);
        return filteredPosts;
      });
      
      console.log('=== MAIN DELETE POST DEBUG END - SUCCESS ===');
      return true;
    } catch (err) {
      console.error('=== MAIN DELETE POST DEBUG - ERROR ===');
      console.error('Error deleting post:', err);
      setPosts(prevPosts => {
        const postIdString = typeof postId === 'number' ? postId.toString() : postId;
        return prevPosts.filter(post => post.id !== postIdString);
      });
      throw err;
    }
  };

  const updatePost = async (updatedPost: Post) => {
    try {
      await updatePostOperation(updatedPost);
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === updatedPost.id ? updatedPost : post
        )
      );
      return true;
    } catch (err) {
      console.error('Error updating post:', err);
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === updatedPost.id ? updatedPost : post
        )
      );
      throw err;
    }
  };

  const toggleLike = async (postId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setPosts(prevPosts => 
          prevPosts.map(post => 
            post.id === postId 
              ? { 
                  ...post, 
                  isLiked: !post.isLiked,
                  likes: post.isLiked ? post.likes - 1 : post.likes + 1
                }
              : post
          )
        );
        return;
      }

      await toggleLikeOperation(postId);
      await fetchPosts();
    } catch (err) {
      console.error('Error toggling like:', err);
      throw err;
    }
  };

  return {
    posts,
    loading,
    error,
    fetchPosts,
    addPost,
    deletePost,
    updatePost,
    toggleLike
  };
};

export type { Post, ZoneFilters };
